import { z } from "zod";

import { type ByteEncoding, decodeToBytes } from "@/lib/crypto";

/**
 * Shared Zod refinement that validates the AES key and IV byte lengths.
 * Used by both the encryption and decryption form schemas so error
 * messages stay identical and live inline under the relevant field.
 */
export function validateKeyIv(
  val: {
    key: string;
    keyEncoding: ByteEncoding;
    iv: string;
    ivEncoding: ByteEncoding;
  },
  ctx: z.RefinementCtx,
): void {
  try {
    const keyBytes = decodeToBytes(val.key, val.keyEncoding);
    if (![16, 24, 32].includes(keyBytes.length)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["key"],
        message: `Key must be 16, 24, or 32 bytes for AES — got ${keyBytes.length}.`,
      });
    }
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["key"],
      message: e instanceof Error ? e.message : "Invalid key.",
    });
  }

  try {
    const ivBytes = decodeToBytes(val.iv, val.ivEncoding);
    if (ivBytes.length !== 16) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["iv"],
        message: `IV must be exactly 16 bytes — got ${ivBytes.length}.`,
      });
    }
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["iv"],
      message: e instanceof Error ? e.message : "Invalid IV.",
    });
  }
}
